import type { Moment } from 'moment';
import moment from 'moment';
import type { Appointment } from 'services/types/appointment';

import { MONTHS, WEEKDAYS_LONG } from './serviceCalendar.constants';

/** Month query param expected by the backend: YYYY-MM. */
export const toMonthParam = (date: Moment): string => date.format('YYYY-MM');

/** 6-week grid (Monday-first) covering the given month. */
export const buildMonthGrid = (anchor: Moment): Moment[] => {
  const start = anchor.clone().startOf('month').startOf('isoWeek');
  return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'days'));
};

/** The 7 days (Monday-first) of the week containing the anchor. */
export const buildWeekDays = (anchor: Moment): Moment[] => {
  const start = anchor.clone().startOf('isoWeek');
  return Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'days'));
};

export const isSameDay = (a: Moment, b: Moment): boolean => a.isSame(b, 'day');

export const formatHourLabel = (hour: number): string => {
  if (hour === 12) return '12 pm';
  if (hour === 0) return '12 am';
  return hour < 12 ? `${hour} am` : `${hour - 12} pm`;
};

export const formatTime = (iso: string): string => moment(iso).format('HH:mm');

export const formatTimeRange = (appointment: Appointment): string => {
  const start = moment(appointment.date);
  const end = start.clone().add(appointment.durationMinutes, 'minutes');
  return `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
};

/** "martes 19 de mayo" style label. */
export const formatLongDate = (date: Moment): string => {
  const weekday = WEEKDAYS_LONG[(date.isoWeekday() + 6) % 7];
  return `${weekday} ${date.date()} de ${MONTHS[date.month()]}`;
};

export const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const monthTitle = (date: Moment): string => `${capitalize(MONTHS[date.month()])} ${date.year()}`;

/** Filter an appointment list to a single calendar day. */
export const appointmentsOnDay = (appointments: Appointment[], day: Moment): Appointment[] =>
  appointments
    .filter((a) => moment(a.date).isSame(day, 'day'))
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

/** Distinct advisors (ejecutivos) present in the appointment set, sorted by name. */
export const distinctAdvisors = (appointments: Appointment[]): string[] => {
  const set = new Set<string>();
  appointments.forEach((a) => {
    if (a.advisorName) set.add(a.advisorName);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};
